/**
 * Gift with Purchase (BOGO) Module
 * Automatically adds a free gift product when cart total exceeds threshold
 *
 * Features:
 * - Auto-add gift when threshold crossed
 * - Auto-remove gift when threshold drops
 * - Non-removable gift (quantity locked at 1)
 * - Debounced checks to prevent rapid API calls
 * - Integration with CartDrawerCoordinator
 * - localStorage state tracking
 */

class GiftWithPurchase {
  constructor(settings) {
    // Configuration from theme settings
    this.config = {
      enabled: settings.enabled || false,
      giftProductId: settings.giftProductId || null,
      giftVariantId: settings.giftVariantId || null,
      threshold: settings.threshold || 1000,
      autoAddedText: settings.autoAddedText || '已自動加入'
    };

    // State management
    this.state = {
      isProcessing: false,
      lastCheckTime: 0,
      giftLineIndex: null,
      checkCount: 0
    };

    // Debounce configuration - increased to prevent rapid firing
    this.debounceDelay = 800; // 800ms debounce (was 500ms)
    this.debounceTimer = null;
    this.minCheckInterval = 500; // Minimum time between checks (was 300ms)

    // localStorage key for gift tracking
    this.storageKey = 'gwp_gift_state';

    if (this.config.enabled && this.config.giftProductId) {
      this.init();
    }
  }

  /**
   * Initialize the gift with purchase system
   */
  init() {
    console.log('🎁 Gift with Purchase initialized', this.config);

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Listen for cart changes
    document.addEventListener('on:cart:add', (evt) => this.handleCartChange(evt));
    document.addEventListener('on:line-item:change', (evt) => this.handleCartChange(evt));
    document.addEventListener('on:cart:change', (evt) => this.handleCartChange(evt));

    // Listen for cart drawer events
    document.addEventListener('dispatch:cart-drawer:open', () => {
      setTimeout(() => this.checkGiftEligibility(), 300);
    });

    // Listen for cart drawer refresh
    document.addEventListener('dispatch:cart-drawer:refresh', () => {
      setTimeout(() => this.checkGiftEligibility(), 300);
    });

    // Initial check if cart drawer is already open
    setTimeout(() => {
      this.checkGiftEligibility();
    }, 1000);
  }

  /**
   * Handle cart change events
   * @param {CustomEvent} evt - Cart change event
   */
  handleCartChange(evt) {
    // Ignore if we're currently processing
    if (this.state.isProcessing) {
      console.log('🎁 Skipping check - already processing');
      return;
    }

    // Ignore events triggered by gift operations themselves
    if (evt && evt.detail && evt.detail.source === 'gwp') {
      console.log('🎁 Skipping check - triggered by gift operation');
      return;
    }

    // Debounce the check with longer delay to prevent rapid firing
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.checkGiftEligibility();
    }, this.debounceDelay);
  }

  /**
   * Check if customer is eligible for gift and add/remove accordingly
   */
  async checkGiftEligibility() {
    // Prevent concurrent checks
    if (this.state.isProcessing) {
      console.log('🎁 Skipping check - already processing');
      return;
    }

    // Prevent too frequent checks
    const now = Date.now();
    if (now - this.state.lastCheckTime < this.minCheckInterval) {
      console.log(`🎁 Skipping check - too soon since last check (${now - this.state.lastCheckTime}ms < ${this.minCheckInterval}ms)`);
      return;
    }

    this.state.lastCheckTime = now;
    this.state.checkCount++;

    console.log(`🎁 Checking gift eligibility (check #${this.state.checkCount})`);

    try {
      this.state.isProcessing = true;

      // Fetch current cart state
      const cart = await this.fetchCart();

      if (!cart) {
        console.warn('🎁 Failed to fetch cart');
        return;
      }

      // Calculate cart subtotal (excluding the gift itself)
      const subtotal = this.calculateSubtotal(cart);
      const isEligible = subtotal >= this.config.threshold;
      const hasGift = this.hasGiftInCart(cart);

      console.log('🎁 Cart Analysis:', {
        subtotal,
        threshold: this.config.threshold,
        isEligible,
        hasGift,
        itemCount: cart.item_count
      });

      // Decide action based on eligibility
      if (isEligible && !hasGift) {
        // Add gift
        await this.addGiftToCart();
      } else if (!isEligible && hasGift) {
        // Remove gift
        await this.removeGiftFromCart(cart);
      } else {
        console.log('🎁 No action needed');
      }

    } catch (error) {
      console.error('🎁 Error checking gift eligibility:', error);
    } finally {
      // Keep processing lock for a bit longer to prevent rapid re-checks
      // This ensures DOM updates and events have time to settle
      setTimeout(() => {
        this.state.isProcessing = false;
        console.log('🎁 Processing lock released');
      }, 200);
    }
  }

  /**
   * Fetch current cart state
   * @returns {Promise<Object>} Cart object
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('🎁 Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Calculate cart subtotal excluding the gift product
   * Excludes both by product_id and by checking for $0 gift variant
   * @param {Object} cart - Cart object
   * @returns {number} Subtotal in cents
   */
  calculateSubtotal(cart) {
    if (!cart || !cart.items) return 0;

    return cart.items.reduce((total, item) => {
      // Exclude the $0 gift variant from subtotal calculation
      if (item.product_id === this.config.giftProductId &&
          item.variant_id === this.config.giftVariantId) {
        return total;
      }
      return total + item.final_line_price;
    }, 0);
  }

  /**
   * Check if gift product is already in cart
   * Uses $0 variant detection instead of property-based detection
   * @param {Object} cart - Cart object
   * @returns {boolean} True if gift is in cart
   */
  hasGiftInCart(cart) {
    if (!cart || !cart.items) return false;

    // Find gift item by checking if it's the $0 variant of the gift product
    const giftItemIndex = cart.items.findIndex(item => {
      return item.product_id === this.config.giftProductId &&
             item.variant_id === this.config.giftVariantId;
    });

    if (giftItemIndex !== -1) {
      // Store the line index for potential removal
      // Shopify cart API uses 1-based line numbers
      this.state.giftLineIndex = giftItemIndex + 1;
      console.log(`🎁 Gift found at line ${this.state.giftLineIndex}`);
      return true;
    }

    this.state.giftLineIndex = null;
    return false;
  }

  /**
   * Add gift product to cart using $0 variant
   * Uses native theme pattern - requests sections and updates DOM once
   * No properties needed - gift is identified by variant_id
   */
  async addGiftToCart() {
    console.log('🎁 Adding gift to cart...');

    try {
      // Prepare form data with sections (like product-form.js does)
      const formData = new FormData();
      formData.append('id', this.config.giftVariantId); // $0 gift variant
      formData.append('quantity', 1);
      // NOTE: No properties needed - $0 variant is inherently identifiable
      // This prevents CVS app conflicts and duplicate cart items

      // Get sections to update (matching native theme pattern)
      let sections = 'cart-icon-bubble';
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      // Add to cart via Shopify API with sections
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Failed to add gift: ${response.status}`);
      }

      const data = await response.json();
      console.log('🎁 Gift added successfully:', data);

      // Update cart drawer using response data (native theme method)
      // No manual refresh needed - response includes updated sections
      if (cartDrawer && data.sections) {
        cartDrawer.renderContents(data, false);
        // Fix gift input value display after render
        setTimeout(() => {
          const giftInput = document.querySelector('[data-is-gift="true"] input[name="updates[]"]');
          if (giftInput) giftInput.value = giftInput.dataset.initialValue || '1';
        }, 50);
      }

      // Update cart icon count (like product-form.js does)
      if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
        ProductForm.updateCartIcon(data);
      }

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('gwp:gift-added', {
        detail: {
          variantId: this.config.giftVariantId,
          productId: this.config.giftProductId
        }
      }));

      // Save state to localStorage
      this.saveState({ added: true, variantId: this.config.giftVariantId });

      // ✅ REMOVED: No manual refresh needed - response data handles it
      // setTimeout(() => { this.refreshCartDrawer(); }, 100);

    } catch (error) {
      console.error('🎁 Error adding gift to cart:', error);

      // Dispatch error event
      document.dispatchEvent(new CustomEvent('gwp:error', {
        detail: {
          action: 'add',
          error: error.message
        }
      }));
    }
  }

  /**
   * Remove gift product from cart
   * Uses native theme pattern - requests sections and updates DOM once
   * @param {Object} cart - Current cart object
   */
  async removeGiftFromCart(cart) {
    if (!this.state.giftLineIndex) {
      console.warn('🎁 Cannot remove gift - line index not found');
      return;
    }

    console.log(`🎁 Removing gift from cart (line ${this.state.giftLineIndex})...`);

    try {
      // Get sections to update (matching native theme pattern)
      let sections = 'cart-icon-bubble';
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Remove via cart change API with sections
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: this.state.giftLineIndex,
          quantity: 0,
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove gift: ${response.status}`);
      }

      const data = await response.json();
      console.log('🎁 Gift removed successfully:', data);

      // Update cart drawer using response data (native theme method)
      if (cartDrawer && data.sections) {
        cartDrawer.renderContents(data, false);
      }

      // Update cart icon count
      if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
        ProductForm.updateCartIcon(data);
      }

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('gwp:gift-removed', {
        detail: {
          variantId: this.config.giftVariantId,
          productId: this.config.giftProductId
        }
      }));

      // Clear state
      this.state.giftLineIndex = null;
      this.clearState();

      // ✅ REMOVED: No manual refresh needed - response data handles it
      // setTimeout(() => { this.refreshCartDrawer(); }, 100);

    } catch (error) {
      console.error('🎁 Error removing gift from cart:', error);

      // Dispatch error event
      document.dispatchEvent(new CustomEvent('gwp:error', {
        detail: {
          action: 'remove',
          error: error.message
        }
      }));
    }
  }

  /**
   * Refresh cart drawer using coordination system
   */
  refreshCartDrawer() {
    // Try to use cart drawer coordinator if available
    if (window.cartDrawerCoordinator) {
      console.log('🎁 Refreshing via cart drawer coordinator');
      window.cartDrawerCoordinator.coordinatedRefresh('gwp', () => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.refresh) {
          return cartDrawer.refresh();
        }
      });
    } else {
      // Fallback to direct cart drawer refresh
      console.log('🎁 Refreshing cart drawer directly');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.refresh) {
        cartDrawer.refresh();
      } else {
        // Fallback to dispatch event
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }
    }

    // Also refresh cart items if on cart page
    const cartItems = document.querySelector('cart-items');
    if (cartItems && cartItems.refresh) {
      cartItems.refresh();
    }
  }

  /**
   * Save gift state to localStorage
   * @param {Object} state - State to save
   */
  saveState(state) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        ...state,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('🎁 Failed to save state to localStorage:', error);
    }
  }

  /**
   * Clear gift state from localStorage
   */
  clearState() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('🎁 Failed to clear state from localStorage:', error);
    }
  }

  /**
   * Get saved state from localStorage
   * @returns {Object|null} Saved state or null
   */
  getSavedState() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) return null;

      const state = JSON.parse(saved);

      // Check if state is still valid (within 24 hours)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in ms
      if (Date.now() - state.timestamp > maxAge) {
        this.clearState();
        return null;
      }

      return state;
    } catch (error) {
      console.warn('🎁 Failed to get saved state from localStorage:', error);
      return null;
    }
  }
}

// Initialize Gift with Purchase when DOM is ready
(function initGiftWithPurchase() {
  // Wait for theme settings to be available
  if (typeof window.Shopify === 'undefined') {
    console.warn('🎁 Shopify object not available, waiting...');
    setTimeout(initGiftWithPurchase, 100);
    return;
  }

  // Get settings from window object (set by theme.liquid)
  const settings = window.gwpSettings || {};

  // Initialize if enabled
  if (settings.enabled && settings.giftProductId) {
    window.giftWithPurchase = new GiftWithPurchase(settings);
  } else {
    console.log('🎁 Gift with Purchase is disabled or not configured');
  }
})();
